import type { ParentMember, TaskPriority, TaskVisibility } from "../lib/types";

export interface TaskFormValue {
  title: string;
  notes: string;
  assignee: string;
  date: string;
  time: string;
  visibility: TaskVisibility;
  priority: TaskPriority;
  points: string;
}

interface TaskFormFieldsProps {
  value: TaskFormValue;
  members: ParentMember[];
  disabled: boolean;
  titleInvalid: boolean;
  allowAssignment?: boolean;
  allowPoints?: boolean;
  onChange: (next: TaskFormValue) => void;
}

export function TaskFormFields({ value, members, disabled, titleInvalid, allowAssignment = true, allowPoints = true, onChange }: TaskFormFieldsProps) {
  const field = <K extends keyof TaskFormValue>(key: K, next: TaskFormValue[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <div className="form-sections">
      <section className="form-card" aria-labelledby="task-details-heading">
        <h2 id="task-details-heading">待辦內容</h2>
        <label className="field field-wide">
          <span>待辦名稱 <b aria-hidden="true">*</b></span>
          <input
            aria-label="待辦名稱"
            autoFocus
            required
            maxLength={120}
            disabled={disabled}
            aria-invalid={titleInvalid}
            value={value.title}
            onChange={(event) => field("title", event.currentTarget.value)}
            placeholder="例如：倒垃圾"
          />
        </label>
        <label className="field field-wide">
          <span>備註 <small>選填</small></span>
          <textarea
            aria-label="備註"
            maxLength={2_000}
            rows={3}
            disabled={disabled}
            value={value.notes}
            onChange={(event) => field("notes", event.currentTarget.value)}
            placeholder="需要記得的細節"
          />
        </label>
      </section>

      <section className="form-card" aria-labelledby="assignment-heading">
        <h2 id="assignment-heading">安排給誰</h2>
        <div className="field-grid">
          <label className="field">
            <span>負責人</span>
            <select aria-label="負責人" disabled={disabled || !allowAssignment} value={value.assignee} onChange={(event) => field("assignee", event.currentTarget.value)}>
              <option value="HOUSEHOLD">全家</option>
              {members.map((member) => <option key={member.memberId} value={member.memberId}>{member.displayName}</option>)}
            </select>
          </label>
          {allowPoints ? <label className="field">
            <span>點數</span>
            <input aria-label="點數" type="number" inputMode="numeric" min="0" max="100" step="1" disabled={disabled} value={value.points} onChange={(event) => field("points", event.currentTarget.value)} />
          </label> : null}
        </div>
      </section>

      <section className="form-card" aria-labelledby="schedule-heading">
        <h2 id="schedule-heading">時間</h2>
        <div className="field-grid">
          <label className="field">
            <span>日期 <small>可留空</small></span>
            <input aria-label="日期" type="date" disabled={disabled} value={value.date} onChange={(event) => {
              const date = event.currentTarget.value;
              onChange({ ...value, date, time: date ? value.time : "" });
            }} />
          </label>
          <label className="field">
            <span>時間 <small>選填</small></span>
            <input aria-label="時間" type="time" disabled={disabled || !value.date} value={value.time} onChange={(event) => field("time", event.currentTarget.value)} />
          </label>
        </div>
        <p className="field-help">沒有日期會放在「未排日期」；只有日期則可在當天結束前完成。</p>
      </section>

      <section className="form-card" aria-labelledby="display-heading">
        <h2 id="display-heading">顯示方式</h2>
        <div className="field-grid">
          <label className="field">
            <span>面板隱私</span>
            <select aria-label="面板隱私" disabled={disabled} value={value.visibility} onChange={(event) => field("visibility", event.currentTarget.value as TaskVisibility)}>
              <option value="PANEL_PUBLIC">面板顯示完整內容</option>
              <option value="PANEL_MASKED">面板只顯示「私人待辦」</option>
              <option value="APP_ONLY">只在手機與網頁顯示</option>
            </select>
          </label>
          <label className="field">
            <span>優先順序</span>
            <select aria-label="優先順序" disabled={disabled} value={value.priority} onChange={(event) => field("priority", event.currentTarget.value as TaskPriority)}>
              <option value="LOW">低</option><option value="NORMAL">一般</option><option value="HIGH">高</option>
            </select>
          </label>
        </div>
        <label className="field field-wide">
          <span>重複</span>
          <select disabled value="ONCE" aria-describedby="recurrence-help"><option value="ONCE">單次</option></select>
        </label>
        <p className="field-help" id="recurrence-help">第一階段先支援單次待辦；固定家務與輪值會在下一階段開放。</p>
      </section>
    </div>
  );
}
